import { DownOutlined } from '@ant-design/icons';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';

interface OscarDisclosureProps {
	/** The always-visible summary line. */
	label: React.ReactNode;
	/** Leading glyph, e.g. a tool status icon. */
	icon?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}

/**
 * Collapsed-by-default detail row for the agent's reasoning, which is long and
 * rarely what the reader wants first — left expanded it buries the answer.
 *
 * Shares its chevron and left rail with `OscarToolTrace` so reasoning and
 * tool calls read as the same kind of thing.
 *
 * A native `<details>`, so it is keyboard accessible and needs no state.
 */
export const OscarDisclosure = ({ label, icon, children, className }: OscarDisclosureProps) => (
	<details className={cn('group w-full min-w-0', className)}>
		<summary
			className="flex cursor-pointer list-none items-center gap-2xs text-character-secondary
				[&::-webkit-details-marker]:hidden"
		>
			<DownOutlined
				// Not `rotate-180`: preflight is off, so Tailwind never emits the
				// universal block that defines --tw-translate-x/skew/scale, and one
				// unresolved var() voids the whole `transform` declaration. Every
				// transform utility in the app is dead for the same reason. An
				// arbitrary value sets `transform` outright and sidesteps it.
				className="shrink-0 text-[10px] transition-transform group-open:[transform:rotate(180deg)]
					motion-reduce:transition-none"
			/>
			{icon}
			<span className="footnoteregular min-w-0 flex-1 truncate">{label}</span>
		</summary>
		<div className="ml-[5px] border-0 border-l border-solid border-grey-4 py-2xs pl-s">
			{children}
		</div>
	</details>
);

export default OscarDisclosure;
